import Icon from '@components/v2/ui/Icon/Icon';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Stack } from '@mui/material';
import { Clock, X } from 'lucide-react';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../../modules/translation/TranslationContext';

const ROW_HEIGHT = 36;
const REMOVE_BUTTON_SIZE = 24;

interface RecentSearchesProps {
  searches: string[];
  onSelect: (query: string) => void;
  onRemove: (query: string) => void;
}

function RecentSearches({ searches, onSelect, onRemove }: RecentSearchesProps) {
  const { translate } = useTranslation();

  return (
    <Box test-id="global-search-recents">
      <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 0.5 }}>
        {translate(LangEnum.GLOBAL_SEARCH_RECENT_TITLE)}
      </Typography>
      <Stack>
        {searches.map((query, index) => (
          <Box
            key={query}
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minHeight: ROW_HEIGHT,
              px: 1,
              borderRadius: `${theme.shape.borderRadiusLg}px`,
              color: theme.palette.text.secondary,
              '&:hover': { backgroundColor: theme.palette.action.hover },
              '& [data-recent-remove]': { opacity: 0 },
              '&:hover [data-recent-remove]': { opacity: 1 },
              '&:focus-within [data-recent-remove]': { opacity: 1 },
              '@media (hover: none)': { '& [data-recent-remove]': { opacity: 1 } },
            })}
          >
            <Box
              component="button"
              type="button"
              test-id={`global-search-recent-item-${index}`}
              onClick={() => onSelect(query)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flex: 1,
                minWidth: 0,
                p: 0,
                border: 'none',
                background: 'none',
                color: 'inherit',
                font: 'inherit',
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              <Icon icon={Clock} size="md" />
              <Typography variant="body1" color="text.primary" noWrap sx={{ flex: 1, minWidth: 0 }}>
                {query}
              </Typography>
            </Box>
            <Box data-recent-remove sx={{ flexShrink: 0 }}>
              <Tooltip
                TooltipContent={translate(LangEnum.GLOBAL_SEARCH_REMOVE_RECENT)}
                tabIndex={-1}
              >
                <IconButton
                  icon={X}
                  ariaLabel={translate(LangEnum.GLOBAL_SEARCH_REMOVE_RECENT)}
                  testId={`global-search-recent-remove-${index}`}
                  sx={{ width: REMOVE_BUTTON_SIZE, height: REMOVE_BUTTON_SIZE, p: 0 }}
                  onClick={() => onRemove(query)}
                />
              </Tooltip>
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

export default RecentSearches;
